import ko from "knockout";
import "knockout.validation";
import koMapping from "knockout-mapping";

function makeObservable<T1, T2>(obj: T1, extraMappingCallback?: (mappedObject: T2) => void): T2 {
    const obsObject = koMapping.fromJS<T1>(obj) as any as T2;

    visit(obsObject);

    if (extraMappingCallback) {
        extraMappingCallback(obsObject);
    }

    return obsObject;
}

function makeObservableArray<T1, T2>(
    array: T1[],
    extraMappingCallback?: (mappedArray: KnockoutObservableArray<T2>) => void
): KnockoutObservableArray<T2> {
    const obsArray = koMapping.fromJS<T1>(array) as any as KnockoutObservableArray<T2>;

    obsArray().forEach(visit);

    if (extraMappingCallback) {
        extraMappingCallback(obsArray);
    }

    return obsArray;
}

function isArray(obj: any) {
    return obj.constructor === Array;
}

function isObject(obj: any) {
    return typeof obj === "object";
}

function isObservableArray(obj: any) {
    return (ko as any).isObservableArray(obj);
}

function visit(value: any) {
    if (value === undefined || value === null) {
        return;
    }

    if (ko.isObservable(value)) {
        visitObservable(value);
    } else if (isObservableArray(value)) {
        visitObservableArray(value);
    } else if (isArray(value)) {
        visitArray(value);
    } else if (isObject(value)) {
        visitObject(value);
    }

    // Do nothing for functions, primitive types
}

function visitObservable(obs: any) {
    obs.extend({
        validatable: true
    });

    const value = ko.unwrap(obs);

    visit(value);
}

function visitObservableArray(obsArray: any) {
    const array = obsArray();
    for (let i = 0; i < array.length; i++) {
        visit(array[i]);
    }
}

function visitArray(array: any[]) {
    for (let i = 0; i < array.length; i++) {
        visit(array[i]);
    }
}

function visitObject(obj: any) {
    for (const prop in obj) {
        visit(obj[prop]);
    }
}

function unmakeObservable<T2, T1>(obsObject: T2): T1 {
    return koMapping.toJS<T2>(obsObject as any as KnockoutObservableType<T2>) as any as T1;
}

function unmakeObservableArray<T2, T1>(obsArray: KnockoutObservableArray<T2>): T1[] {
    return koMapping.toJS<T1>(obsArray as any as KnockoutObservableType<T1>) as any as T1[];
}

export default {
    makeObservable: makeObservable,
    unmakeObservable: unmakeObservable,
    makeObservableArray: makeObservableArray,
    unmakeObservableArray: unmakeObservableArray
};
